import { useEffect, useMemo, useRef, useState } from 'react';
import './birthday.css';
import lockShieldImg from '../assets/icon.png';

const ITEM_HEIGHT = 48;
const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
const MIN_AGE = 13;

function ChevronLeftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CalendarIcon({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="3.5" y="5" width="17" height="15" rx="3" stroke="currentColor" strokeWidth="1.8" />
      <path d="M3.5 9.5h17" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <rect x="7" y="12.2" width="3" height="3" rx="0.6" fill="currentColor" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 3l7 3v5c0 5-3.4 8.4-7 10-3.6-1.6-7-5-7-10V6l7-3Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function WheelColumn({ items, selectedIndex, onSelect }) {
  const containerRef = useRef(null);
  const scrollTimeout = useRef(null);
  const isProgrammaticScroll = useRef(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    isProgrammaticScroll.current = true;
    el.scrollTop = selectedIndex * ITEM_HEIGHT;
    const id = setTimeout(() => {
      isProgrammaticScroll.current = false;
    }, 50);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleScroll() {
    if (isProgrammaticScroll.current) return;
    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    scrollTimeout.current = setTimeout(() => {
      const el = containerRef.current;
      if (!el) return;
      const index = Math.min(
        items.length - 1,
        Math.max(0, Math.round(el.scrollTop / ITEM_HEIGHT))
      );
      onSelect(index);
    }, 100);
  }

  function handleItemClick(index) {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTo({ top: index * ITEM_HEIGHT, behavior: 'smooth' });
    onSelect(index);
  }

  return (
    <div className="birthday-wheel-col" ref={containerRef} onScroll={handleScroll}>
      {items.map((item, index) => (
        <div
          key={item.value}
          className={`birthday-wheel-item ${index === selectedIndex ? 'selected' : ''}`}
          onClick={() => handleItemClick(index)}
        >
          {item.label}
        </div>
      ))}
    </div>
  );
}

function calculateAge(day, month, year) {
  const today = new Date();
  const birth = new Date(year, month - 1, day);
  let age = today.getFullYear() - birth.getFullYear();
  const hasHadBirthdayThisYear =
    today.getMonth() > birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() >= birth.getDate());
  if (!hasHadBirthdayThisYear) age -= 1;
  return age;
}

export default function BirthdayStep({ onBack, onContinue }) {
  const days = useMemo(
    () => Array.from({ length: 31 }, (_, i) => ({ value: i + 1, label: String(i + 1).padStart(2, '0') })),
    []
  );
  const months = useMemo(
    () => MONTH_NAMES.map((name, i) => ({ value: i + 1, label: name })),
    []
  );
  const currentYear = new Date().getFullYear();
  const years = useMemo(() => {
    const list = [];
    for (let y = currentYear - 100; y <= currentYear; y += 1) {
      list.push({ value: y, label: String(y) });
    }
    return list;
  }, [currentYear]);

  const [dayIndex, setDayIndex] = useState(21); // 22
  const [monthIndex, setMonthIndex] = useState(6); // Jul
  const [yearIndex, setYearIndex] = useState(years.findIndex((y) => y.value === 2005));
  const [showPicker, setShowPicker] = useState(false);

  const selectedDay = days[dayIndex].value;
  const selectedMonth = months[monthIndex].value;
  const selectedYear = years[yearIndex]?.value ?? currentYear - 20;

  const age = calculateAge(selectedDay, selectedMonth, selectedYear);
  const isValid = age >= MIN_AGE;

  const formattedDate = `${String(selectedDay).padStart(2, '0')} / ${String(selectedMonth).padStart(2, '0')} / ${selectedYear}`;

  return (
    <div className="birthday-screen">
      <div className="birthday-frame">
        <div className="birthday-header">
          <button type="button" className="birthday-back-btn" onClick={onBack} aria-label="Go back">
            <ChevronLeftIcon />
          </button>

          <div className="birthday-glow" />
          <div className="birthday-icon-wrap">
            <img src={lockShieldImg} alt="" className="birthday-icon-img" />
          </div>
          <h1 className="birthday-title">When&apos;s your birthday?</h1>
          <p className="birthday-subtitle">This helps us personalize your experience</p>
        </div>

        <form
          className="birthday-card"
          onSubmit={(e) => {
            e.preventDefault();
            if (isValid) onContinue?.({ day: selectedDay, month: selectedMonth, year: selectedYear });
          }}
        >
          <div className="birthday-calendar-icon">
            <CalendarIcon />
          </div>
          <h2 className="birthday-card-title">Select your date of birth</h2>
          <p className="birthday-card-subtitle">
            You must be at least {MIN_AGE} years old to create an account
          </p>

          <button
            type="button"
            className="birthday-input-wrap"
            onClick={() => setShowPicker((v) => !v)}
            aria-expanded={showPicker}
          >
            <CalendarIcon size={20} />
            <span className="birthday-date-text">{formattedDate}</span>
            <span className={`birthday-input-chevron ${showPicker ? 'open' : ''}`}>
              <ChevronDownIcon />
            </span>
          </button>

          {showPicker && (
            <div className="birthday-wheel">
              <div className="birthday-wheel-highlight" />
              <WheelColumn items={days} selectedIndex={dayIndex} onSelect={setDayIndex} />
              <WheelColumn items={months} selectedIndex={monthIndex} onSelect={setMonthIndex} />
              <WheelColumn items={years} selectedIndex={yearIndex} onSelect={setYearIndex} />
            </div>
          )}

          <p className="birthday-privacy-note">
            <ShieldIcon />
            Your date of birth is private and will not be shared
          </p>

          <button type="submit" className="birthday-submit" disabled={!isValid}>
            Continue <ArrowRightIcon />
          </button>
        </form>
      </div>
    </div>
  );
}
